
import { GoogleGenAI, Type, GenerateContentResponse } from "@google/genai";
import { SupportedLanguage, User, Message } from '../types';

const getSystemInstruction = (user: User, language: SupportedLanguage) => 
  `You are the VitaCore AI Coach. Help ${user.name} with fitness and nutrition. Respond in ${language}. 
   User Data: Gender ${user.gender || 'Not specified'}, Age ${user.age || 'Not provided'}, Height ${user.height || 'Not provided'}cm, Weight ${user.weight || 'Not provided'}kg, Allergies ${user.allergies || 'None'}. 
   Be concise, motivating, and scientific.`;

export const chatWithCoach = async (message: string, language: SupportedLanguage, user: User, history: Message[] = []) => {
  try {
    // Initializing GoogleGenAI inside the function to ensure the current environment API_KEY is used.
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const chat = ai.chats.create({
      model: 'gemini-3-flash-preview',
      config: {
        systemInstruction: getSystemInstruction(user, language),
        temperature: 0.7,
      },
      history: history.map(m => ({
        role: m.role,
        parts: [{ text: m.text }]
      }))
    });

    const result: GenerateContentResponse = await chat.sendMessage({ message });
    return {
      text: result.text || "I'm not sure how to respond to that.",
      groundingChunks: result.candidates?.[0]?.groundingMetadata?.groundingChunks || []
    };
  } catch (error) {
    console.error("Chat error:", error);
    throw error;
  }
};

export const generateWorkoutPlan = async (goal: string, equipment: string, user: User, language: SupportedLanguage) => {
  try {
    // Initializing GoogleGenAI inside the function as per the coding guidelines for the SDK.
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: `Weekly workout for ${goal} using ${equipment}. User is ${user.gender || 'unspecified'}, ${user.age || 'unspecified'} years old, ${user.height || 'unspecified'}cm tall, ${user.weight || 'unspecified'}kg. Language: ${language}.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            days: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  day: { type: Type.STRING },
                  focus: { type: Type.STRING },
                  exercises: {
                    type: Type.ARRAY,
                    items: {
                      type: Type.OBJECT,
                      properties: {
                        name: { type: Type.STRING },
                        sets: { type: Type.INTEGER },
                        reps: { type: Type.STRING },
                        notes: { type: Type.STRING }
                      },
                      required: ["name", "sets", "reps"]
                    }
                  }
                },
                required: ["day", "focus", "exercises"]
              }
            }
          },
          required: ["title", "days"]
        }
      }
    });
    return JSON.parse(response.text || '{}');
  } catch (error) {
    throw error;
  }
};

export const generateDietPlan = async (goal: string, dietType: string, user: User, language: SupportedLanguage) => {
  try {
    // Initializing GoogleGenAI inside the function to ensure it uses the latest process.env.API_KEY.
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: `Create a daily diet plan for ${user.name}. Gender: ${user.gender || 'unspecified'}, Age: ${user.age || 'unspecified'}, Height: ${user.height || 'unspecified'}cm, Weight: ${user.weight || 'unspecified'}kg. Goal: ${goal}, Type: ${dietType}. Language: ${language}.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            totalCalories: { type: Type.INTEGER },
            totalMacros: {
              type: Type.OBJECT,
              properties: {
                protein: { type: Type.INTEGER },
                carbs: { type: Type.INTEGER },
                fats: { type: Type.INTEGER }
              }
            },
            meals: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING },
                  time: { type: Type.STRING },
                  foods: { type: Type.ARRAY, items: { type: Type.STRING } },
                  macros: {
                    type: Type.OBJECT,
                    properties: {
                      protein: { type: Type.INTEGER },
                      carbs: { type: Type.INTEGER },
                      fats: { type: Type.INTEGER },
                      calories: { type: Type.INTEGER }
                    }
                  }
                },
                required: ["name", "time", "foods", "macros"]
              }
            }
          },
          required: ["title", "totalCalories", "totalMacros", "meals"]
        }
      }
    });
    return JSON.parse(response.text || '{}');
  } catch (error) {
    throw error;
  }
};
